import API from '../../../APIs/API';
import {ParameterStore} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {TemplateDataFields} from '../../ContactForm/EmailEditor/EmailEditor';
import {fieldOptions} from '../../../Core/mobx/ParameterStore/QuoteXref';
import Utils from '../../../Core/Utils';

// Helper function to get API code from parameter value
function getApiCode(paramId: string, value: any): string {
    if (fieldOptions.hasOwnProperty(paramId)) {
        const fieldOption = fieldOptions[paramId as keyof typeof fieldOptions] as any;
        if (fieldOption.optionValues) {
            const index = fieldOption.options.findIndex((o: string) => Utils.MakeVarName(o) === value);
            if (index > -1) {
                return fieldOption.optionValues[index];
            }
        }
    }
    return value?.toString() || '';
}

export const tafeAPI = async (parameterStore: ParameterStore, formType?: 'single-quote' | 'combination-quote') => {
    let quotes = parameterStore.quote;
    if (formType === 'combination-quote' && parameterStore.quoteCombination) {
        quotes = parameterStore.quoteCombination;
    }

    // Fetch info from form
    const age = parameterStore.prequal.age.value;
    const gender = parameterStore.approval.gender.value;
    const state = parameterStore.approval.state.value;
    const healthRatingTAFE = getApiCode('healthRatingTAFE', quotes.healthRatingTAFE.value); // 'NonSmoker' or 'Smoker'
    const solveForTAFE = getApiCode('solveForTAFE', quotes.solveForTAFE.value); // 'P' or 'C'
    const specifiedPremiumTAFE = quotes.specifiedPremiumTAFE.value;
    const specifiedFaceAmountTAFE = getApiCode('specifiedFaceAmountTAFE', quotes.specifiedFaceAmountTAFE.value); // numeric string like '10000'

    // TAFE is not available in NY
    if (state === 'NY') {
        return {
            error: 'TA FE Express is not available in New York state.',
        };
    }

    // Load TAFE rate table
    let data: {
        state_id: string;
        gender_id: string;
        insured_age: number;
        face_amount: number;
        modal_premium: number;
        rating_class: string;
    }[] = [];

    try {
        data =
            (await API.RequestToThisOrigin(
                `./guaranteed-data/tafe.json?v=${ParameterStore.version}`,
                'GET',
                undefined,
            )) || [];
    } catch (error) {
        return {
            error: 'Failed to load TAFE rate table. Please try again.',
        };
    }

    // Find matching quote based on: age, gender, rating_class, premium amount
    // Since we're solving for Face Amount based on Premium, we need to find the closest match
    const matchingRecords = data.filter(
        (el) => el.insured_age === age && el.gender_id === gender && el.rating_class === healthRatingTAFE,
    );

    if (matchingRecords.length === 0) {
        return {
            error: 'Could not find products with the provided information.',
        };
    }

    let selectedRecord;

    if (solveForTAFE === 'C') {
        // Solve for Coverage (find Face Amount based on Premium)
        const premiumAmount = parseFloat(specifiedPremiumTAFE as string);

        if (isNaN(premiumAmount) || premiumAmount <= 0) {
            return {
                error: 'Please specify a valid premium amount.',
            };
        }

        // Find the record with premium closest to the specified amount
        let closestRecord = matchingRecords[0];
        let minDiff = Math.abs(matchingRecords[0].modal_premium - premiumAmount);

        for (const record of matchingRecords) {
            const diff = Math.abs(record.modal_premium - premiumAmount);
            if (diff < minDiff) {
                minDiff = diff;
                closestRecord = record;
            }
        }

        // Check if the match is reasonably close (within $5)
        if (minDiff > 5) {
            return {
                error: `No exact match found for premium $${premiumAmount.toFixed(2)}. Closest available is $${closestRecord.modal_premium.toFixed(2)} for $${closestRecord.face_amount.toLocaleString()} coverage.`,
            };
        }

        selectedRecord = closestRecord;
    } else {
        // Solve for Premium (find Premium based on Face Amount)
        // specifiedFaceAmountTAFE is already a numeric string from optionValues (e.g., '10000')
        const faceAmount = parseInt(specifiedFaceAmountTAFE as string, 10);

        if (isNaN(faceAmount) || faceAmount <= 0) {
            return {
                error: 'Please specify a valid face amount.',
            };
        }

        // Find exact match for face amount
        const exactMatch = matchingRecords.find((record) => record.face_amount === faceAmount);

        if (!exactMatch) {
            return {
                error: `No rate found for $${faceAmount.toLocaleString()} coverage.`,
            };
        }

        selectedRecord = exactMatch;
    }

    return {
        faceAmount: selectedRecord.face_amount,
        monthlyPremium: selectedRecord.modal_premium,
        productName: 'TA FE Express',
        out: [
            {
                name: 'Carrier',
                value: 'Transamerica',
                type: 'string',
                fieldName: TemplateDataFields.carrier,
            },
            {
                name: 'Product',
                value: 'TA FE Express',
                type: 'string',
                fieldName: TemplateDataFields.productName,
            },
            {
                name: 'Face Amount',
                value: selectedRecord.face_amount,
                type: 'currency',
                fieldName: TemplateDataFields.faceAmount,
            },
            {
                name: 'Monthly Premium',
                value: selectedRecord.modal_premium,
                type: 'currency',
                fieldName: TemplateDataFields.monthlyPremium,
            },
            {
                name: 'Risk Class',
                value: healthRatingTAFE === 'NonSmoker' ? 'Non-Smoker' : 'Smoker',
                type: 'string',
            },
            {
                name: 'Valid',
                value: 'valid',
                type: 'string',
            },
        ],
        requestDetails: {
            age,
            gender,
            healthRatingTAFE,
            solveForTAFE,
            ...(solveForTAFE === 'C' ? {specifiedPremiumTAFE} : {specifiedFaceAmountTAFE}),
        },
    };
};
